import { extractCanonicalKey, canonicalKeyToUrl } from './pathNormalizer';

// Upload-backed artwork must render through first-party app URLs, not raw S3 origins.
// This keeps saved battle-room state resilient to bucket/CDN/CORS changes and lets older
// persisted records heal on read by collapsing any upload-like reference back to the proxy.
const isNonEmptyString = (value) => typeof value === 'string' && value.trim().length > 0;

export function buildUploadProxyUrl(key) {
  const canonicalKey = extractCanonicalKey(key);
  if (!canonicalKey) return null;
  return `/api/s3-uploads/proxy?key=${encodeURIComponent(canonicalKey)}`;
}

export function buildUploadCanonicalUrl(key) {
  const canonicalKey = extractCanonicalKey(key);
  if (!canonicalKey) return null;
  return canonicalKeyToUrl(canonicalKey);
}

export function extractUploadKeyFromAsset(asset) {
  if (!asset || typeof asset !== 'object') return null;

  const candidates = [
    asset.key,
    asset.s3Key,
    asset.tokenAssetKey,
    asset.tokenAsset?.key,
    asset.proxyUrl,
    asset.url,
    asset.directUrl,
    asset.artwork,
    asset.imageUrl
  ];

  for (const candidate of candidates) {
    const key = extractCanonicalKey(candidate);
    if (key) return key;
  }

  return null;
}

export function resolveUploadAssetUrl(input) {
  if (!input) return input;

  if (isNonEmptyString(input)) {
    const key = extractCanonicalKey(input);
    return key ? (buildUploadProxyUrl(key) || buildUploadCanonicalUrl(key) || input) : input;
  }

  if (typeof input === 'object') {
    const key = extractUploadKeyFromAsset(input);
    if (key) {
      return buildUploadProxyUrl(key) || buildUploadCanonicalUrl(key) || input.proxyUrl || input.url || input.directUrl || '';
    }
    return input.proxyUrl || input.url || input.directUrl || '';
  }

  return input;
}

export function normalizeUploadedAsset(asset) {
  if (!asset || typeof asset !== 'object') return asset;

  const key = extractUploadKeyFromAsset(asset) || asset.key || asset.s3Key || null;
  const canonicalUrl = key ? buildUploadCanonicalUrl(key) : null;
  const proxyUrl = key ? buildUploadProxyUrl(key) : (asset.proxyUrl || null);
  const fallbackUrl = asset.url || asset.directUrl || proxyUrl || canonicalUrl || '';
  const url = proxyUrl || canonicalUrl || fallbackUrl;

  return {
    ...asset,
    ...(key ? { key } : {}),
    canonicalUrl,
    proxyUrl,
    url,
    fallbackUrl,
  };
}
